import React from 'react';
import { Award, Users, TrendingUp, Target, Briefcase, BarChart3 } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const stats = [
  { icon: TrendingUp, value: '€2.5M+', label: 'Fatturato generato' },
  { icon: Users, value: '45+', label: 'Aziende seguite' },
  { icon: Target, value: '89%', label: 'Tasso di successo' },
  { icon: BarChart3, value: '180%', label: 'ROI medio clienti' },
];

const highlights = [
  {
    icon: Briefcase,
    title: 'Dal 2011 nel settore',
    text: "Oltre 14 anni dedicati esclusivamente alla consulenza strategica per aziende che vogliono dominare il proprio mercato.",
  },
  {
    icon: Target,
    title: 'Solo clienti selezionati',
    text: "Non lavoro con tutti. Accetto solo 4 clienti alla volta per garantire la massima dedizione e risultati eccezionali.",
  },
  {
    icon: TrendingUp,
    title: 'Risultati misurabili',
    text: "Ogni strategia è costruita su dati concreti e KPI chiari. Niente teoria, solo risultati tangibili sul fatturato.",
  },
  {
    icon: Award,
    title: 'Track record comprovato',
    text: "Ho aiutato oltre 45 aziende a superare i propri obiettivi. L'89% dei miei clienti migliora significativamente il fatturato entro 18 mesi.",
  },
];

const AboutSection = () => {
  const [titleRef, titleVisible] = useScrollAnimation();
  const [imageRef, imageVisible] = useScrollAnimation();
  const [statsRef, statsVisible] = useScrollAnimation();
  const [highlightsRef, highlightsVisible] = useScrollAnimation();

  return (
    <section id="about" className="bg-[#e2e8f0] py-16 sm:py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        
        {/* Top Section - Title + Image */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center mb-12 sm:mb-20">
          {/* Left Content */}
          <div 
            ref={titleRef}
            className={`animate-fade-left ${titleVisible ? 'animated' : ''}`}
          >
            <p className="text-[#d4883a] font-semibold mb-3 sm:mb-4 tracking-wide text-sm sm:text-base">Chi sono</p>
            <h2 
              className="text-3xl sm:text-4xl lg:text-6xl text-[#0b114e] leading-tight mb-4 sm:mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Il consulente che le aziende{' '}
              <span className="text-[#d4883a]">ambiziose</span>{' '}
              scelgono per vincere.
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
              Sono Nicola Scerni. Dal 2011 aiuto imprenditori e CEO a costruire strategie di marketing che non solo funzionano, ma che trasformano radicalmente il posizionamento e i risultati delle loro aziende.
            </p>
          </div>

          {/* Right - Image */}
          <div 
            ref={imageRef}
            className={`relative animate-fade-right ${imageVisible ? 'animated' : ''}`}
          >
            <div className="relative group">
              {/* Main Image */}
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/media/nicola-about.webp"
                  alt="Nicola Scerni"
                  className="w-full h-auto transition-transform duration-700 ease-out group-hover:scale-105"
                />
                {/* Overlay gradient - si schiarisce leggermente all'hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b114e]/30 via-transparent to-transparent transition-opacity duration-500 group-hover:opacity-70" />
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#d4883a]/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-[#0b114e]/10 rounded-full blur-2xl" />
              
              {/* Floating badge - positioned on the RIGHT side, bottom */}
              <div className="absolute -bottom-6 -right-6 z-20 bg-white rounded-2xl p-5 shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#d4883a]/10 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#d4883a]" />
                  </div>
                  <div>
                    <p className="text-[#0b114e] font-bold text-lg">14+</p>
                    <p className="text-gray-500 text-sm">anni di esperienza</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div 
          ref={statsRef}
          className={`bg-[#0b114e] rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 mb-12 sm:mb-20 animate-on-scroll ${statsVisible ? 'animated' : ''}`}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-xl mb-2 sm:mb-4">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4883a]" />
                  </div>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-white/60 text-xs sm:text-sm">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Highlights Grid */}
        <div 
          ref={highlightsRef}
          className="grid md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8"
        >
          {highlights.map((item, index) => {
            const Icon = item.icon;
            return (
              <div 
                key={index}
                className={`group flex gap-4 sm:gap-5 p-4 sm:p-6 bg-gray-50 rounded-xl sm:rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-gray-100 animate-on-scroll stagger-${index + 1} ${highlightsVisible ? 'animated' : ''}`}
              >
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#d4883a]/10 rounded-xl flex items-center justify-center group-hover:bg-[#d4883a]/20 transition-colors">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4883a]" />
                  </div>
                </div>
                <div>
                  <h3 className="text-[#0b114e] font-bold text-base sm:text-lg mb-1 sm:mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{item.text}</p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default AboutSection;
